import React from 'react';
import { Box, Button, Typography, IconButton } from '@mui/material';
import {
  Close,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';

/**
 * Reusable View/Form Page Header Component
 * Provides consistent header for view and form pages (CustomerView, ProductForm, etc.)
 * Matches Odoo-style design pattern
 * 
 * @param {string} parentTitle - Parent page title (e.g., "Customers", "Products")
 * @param {string} currentTitle - Current item title
 * @param {function} onParentClick - Callback when parent title is clicked
 * @param {function} onNewClick - Callback when New button is clicked
 * @param {function} onClose - Callback when close button is clicked
 * @param {boolean} showNewButton - Whether to show New button (default: true)
 * @param {React.ReactNode} statsButtons - Stats buttons to render
 * @param {object} pagination - Pagination config { current, total, onPrev, onNext }
 * @param {React.ReactNode} additionalActions - Additional actions after stats
 * @param {object} sx - Additional styles
 */
const ViewPageHeader = ({
  parentTitle,
  currentTitle,
  onParentClick,
  onNewClick,
  onClose,
  showNewButton = true,
  statsButtons,
  pagination,
  additionalActions,
  sx = {},
}) => {
  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderBottom: '1px solid #dee2e6',
        px: 2,
        py: 0.75,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'nowrap',
        overflowX: 'auto',
        ...sx,
      }}
    >
      {/* Left - New button and breadcrumb */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        {showNewButton && onNewClick && (
          <Button
            variant="outlined"
            onClick={onNewClick}
            sx={{
              borderColor: '#dee2e6',
              color: '#333',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              px: 2,
              py: 0.5,
              minHeight: 32,
              '&:hover': { borderColor: '#875A7B', backgroundColor: 'transparent' },
            }}
          >
            New
          </Button>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
          <Typography
            sx={{
              fontSize: '13px',
              color: '#875A7B',
              fontWeight: 500,
              cursor: onParentClick ? 'pointer' : 'default',
            }}
            onClick={onParentClick}
          >
            {parentTitle}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#333', fontWeight: 500 }}>
            {currentTitle || 'New'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          {onClose && (
            <IconButton size="small" sx={{ p: 0.25 }} onClick={onClose}>
              <Close sx={{ fontSize: 16, color: '#666' }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Stats buttons */}
      {statsButtons && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 0,
            flexShrink: 0,
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            overflow: 'hidden',
            ml: 'auto',
          }}
        >
          {statsButtons}
        </Box>
      )}

      {/* Additional actions */}
      {additionalActions}

      {/* Pagination */}
      {pagination && pagination.total > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: statsButtons ? 1 : 'auto' }}>
          <Typography sx={{ fontSize: '13px', color: '#666' }}>
            {pagination.current} / {pagination.total}
          </Typography>
          <IconButton
            size="small"
            disabled={pagination.current <= 1}
            onClick={pagination.onPrev}
            sx={{ p: 0.25 }}
          >
            <ChevronLeft sx={{ fontSize: 18, color: pagination.current <= 1 ? '#ccc' : '#666' }} />
          </IconButton>
          <IconButton
            size="small"
            disabled={pagination.current >= pagination.total}
            onClick={pagination.onNext}
            sx={{ p: 0.25 }}
          >
            <ChevronRight sx={{ fontSize: 18, color: pagination.current >= pagination.total ? '#ccc' : '#666' }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

/**
 * Stat Button Component for use inside ViewPageHeader
 */
export const StatButton = ({
  icon,
  title,
  value,
  onClick,
  isLast = false,
}) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.5,
      py: 0.5,
      cursor: onClick ? 'pointer' : 'default',
      borderRight: isLast ? 'none' : '1px solid #dee2e6',
      '&:hover': onClick ? { bgcolor: '#f8f9fa' } : {},
    }}
  >
    {icon}
    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
      <Typography sx={{ fontSize: '11px', color: '#333' }}>{title}</Typography>
      <Typography sx={{ fontSize: '10px', color: '#666' }}>{value}</Typography>
    </Box>
  </Box>
);

export default ViewPageHeader;
